<script setup lang="ts">
import type { DropdownOption } from "@/redistribution/models/DropdownOption.ts";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useId,
  watch,
} from "vue";

defineOptions({
  name: "BaseDropdown",
});

const properties = withDefaults(
  defineProps<{
    id?: string;
    ariaLabel?: string;
    options: DropdownOption[];
    placeholder?: string;
    loading?: boolean;
    error?: string;
    filterable?: boolean;
    multiselect?: boolean;
    disabled?: boolean;
    /** Keep overlays inside an owning non-modal settings surface when supplied. */
    teleportTo?: string | HTMLElement;
    showGroupHeaders?: boolean;
    getGroupHeaderLabel?: (group: string) => string;
    getGroupBy?: (option: DropdownOption) => string;
    groupSortComparator?: (a: string, b: string) => number;
    optionSortComparator?: (a: DropdownOption, b: DropdownOption) => number;
    emptyStateText?: string;
    loadingStateText?: string;
    autoSelectWhen?: (filterText: string) => boolean;
    onGroupHeaderClick?: (group: string) => void;
  }>(),
  {
    filterable: false,
    multiselect: false,
    disabled: false,
    teleportTo: "body",
    showGroupHeaders: false,
    getGroupHeaderLabel: (group: string) => group,
    getGroupBy: () => "",
    emptyStateText: "No results found.",
    loadingStateText: "Loading. Please wait.",
  }
);

const model = defineModel<string | undefined>();
const multiModel = defineModel<string[]>("multiModel", { default: () => [] });

const selectedMultiOptions = computed(() =>
  properties.options.filter((o) => multiModel.value.includes(o.value))
);

const isMultiSelected = (value: string) => multiModel.value.includes(value);

const toggleMultiOption = (value: string) => {
  const opt = properties.options.find((o) => o.value === value);
  if (opt?.disabled) return;
  multiModel.value = isMultiSelected(value)
    ? multiModel.value.filter((v) => v !== value)
    : [...multiModel.value, value];
};

const removeMultiOption = (value: string, e: MouseEvent) => {
  e.stopPropagation();
  multiModel.value = multiModel.value.filter((v) => v !== value);
};

const clearAllMulti = (e: MouseEvent) => {
  e.stopPropagation();
  multiModel.value = [];
};

const isOpen = ref(false);
const highlightedIndex = ref<number | undefined>();
const overlayId = useId();
const triggerRef = ref<HTMLElement | undefined>();
const overlayRef = ref<HTMLElement | undefined>();
const iconRef = ref<SVGElement | undefined>();
const filterText = ref("");
const inputRef = ref<HTMLInputElement | undefined>();
const autoCompletedText = ref("");
const suppressAutoCompleteFromDelete = ref(false);
const highlightedWithKeyboard = ref(false);
const skipAutoSelectOnNextBlur = ref(false);

let autoCompleteTimeout: ReturnType<typeof setTimeout> | undefined;
let autoSelectTimeout: ReturnType<typeof setTimeout> | undefined;
const AUTO_SELECT_DELAY_MS = 1500;

const clearAutoSelectTimeout = () => {
  if (autoSelectTimeout) {
    clearTimeout(autoSelectTimeout);
    autoSelectTimeout = undefined;
  }
};

const defaultGroupSortComparator = (a: string, b: string) => {
  const labelA = properties.getGroupHeaderLabel(a);
  const labelB = properties.getGroupHeaderLabel(b);
  return labelA.localeCompare(labelB);
};

const defaultOptionSortComparator = (a: DropdownOption, b: DropdownOption) =>
  a.label.localeCompare(b.label);

const filteredGroups = computed(() => {
  const groupMap: Record<string, DropdownOption[]> = {};
  for (const option of filteredOptions.value) {
    const groupKey = properties.getGroupBy(option);
    groupMap[groupKey] ??= [];
    groupMap[groupKey].push(option);
  }

  const groupComparator =
    properties.groupSortComparator ?? defaultGroupSortComparator;
  const optionComparator =
    properties.optionSortComparator ?? defaultOptionSortComparator;

  const sortedKeys = Object.keys(groupMap).toSorted(groupComparator);

  // Build sorted group object
  const sortedGroups: Record<string, DropdownOption[]> = {};
  for (const key of sortedKeys) {
    sortedGroups[key] = (groupMap[key] ?? []).toSorted(optionComparator);
  }
  return sortedGroups;
});

const displayedOptions = computed<DropdownOption[]>(() => {
  const flattened: DropdownOption[] = [];
  for (const options of Object.values(filteredGroups.value)) {
    flattened.push(...options);
  }
  return flattened;
});

const filteredOptions = computed(() => {
  if (!properties.filterable || !filterText.value.trim()) {
    return properties.options;
  }
  const lowerFilter = filterText.value.toLowerCase();
  return properties.options.filter((o) =>
    o.label.toLowerCase().startsWith(lowerFilter)
  );
});

const overlayStyle = ref<Record<string, string>>({});
const OVERLAY_Z_INDEX = "20000";

const placeholder = computed(() => {
  if (properties.disabled) {
    return "";
  }
  if (properties.loading) {
    return "Loading…";
  }
  if (properties.error) {
    return properties.error;
  }
  return model.value === undefined
    ? (properties.placeholder ?? "Select an option")
    : undefined;
});

const toggle = () => {
  if (properties.disabled) {
    close();
    return;
  }

  if (isOpen.value) {
    close();
  } else {
    open();
  }
};

const open = () => {
  if (properties.disabled) {
    return;
  }

  isOpen.value = true;
  highlightedIndex.value = undefined;
  highlightedWithKeyboard.value = false;
  void nextTick(() => {
    positionOverlay();
    if (properties.filterable && inputRef.value) {
      inputRef.value.focus();
      if (!properties.multiselect) {
        inputRef.value.select();
      }
    }
  });
};

const close = () => {
  clearAutoSelectTimeout();
  isOpen.value = false;
  highlightedIndex.value = undefined;
  highlightedWithKeyboard.value = false;
};

const closePreservingAutoSelect = () => {
  isOpen.value = false;
};

const select = (option: DropdownOption) => {
  if (properties.disabled || option.disabled) {
    return;
  }

  if (properties.multiselect) {
    toggleMultiOption(option.value);
    return;
  }
  model.value = option.value;
  filterText.value = option.label;
  close();
};
watch(filterText, (newVal) => {
  if (newVal.trim() === "") {
    model.value = undefined;
  }
});
watch(
  [model, () => properties.options] as const,
  ([newModel, newOptions]) => {
    if (!properties.filterable || properties.multiselect) return;
    if (newModel !== undefined && filterText.value === "") {
      const match = newOptions.find((o) => o.value === newModel);
      if (match) {
        filterText.value = match.label;
      }
    }
  },
  { immediate: true }
);
watch(
  [filteredOptions, () => suppressAutoCompleteFromDelete.value],
  ([options, isDeleteSuppressed]) => {
    clearAutoSelectTimeout();
    const filterTextValue = filterText.value;
    const shouldAutoSelect = properties.autoSelectWhen
      ? properties.autoSelectWhen(filterTextValue)
      : false;
    if (
      !properties.filterable ||
      properties.multiselect ||
      isDeleteSuppressed ||
      !isOpen.value ||
      filterTextValue === "" ||
      options.length !== 1 ||
      !options[0] ||
      !shouldAutoSelect
    ) {
      return;
    }

    const targetOption = options[0];
    autoSelectTimeout = setTimeout(() => {
      autoSelectTimeout = undefined;
      const deferredShouldAutoSelect = properties.autoSelectWhen
        ? properties.autoSelectWhen(filterText.value)
        : false;
      if (
        filteredOptions.value.length === 1 &&
        filteredOptions.value[0]?.value === targetOption.value &&
        deferredShouldAutoSelect
      ) {
        select(targetOption);
      }
    }, AUTO_SELECT_DELAY_MS);
  }
);

const scheduleAutoSelectOnBlur = () => {
  clearAutoSelectTimeout();

  const filterTextValue = filterText.value;
  const shouldAutoSelect = properties.autoSelectWhen
    ? properties.autoSelectWhen(filterTextValue)
    : false;
  const targetOption = filteredOptions.value[0];

  if (
    !properties.filterable ||
    properties.multiselect ||
    suppressAutoCompleteFromDelete.value ||
    filterTextValue === "" ||
    filteredOptions.value.length !== 1 ||
    !targetOption ||
    !shouldAutoSelect
  ) {
    return;
  }

  autoSelectTimeout = setTimeout(() => {
    autoSelectTimeout = undefined;
    const deferredShouldAutoSelect = properties.autoSelectWhen
      ? properties.autoSelectWhen(filterText.value)
      : false;

    if (
      filteredOptions.value.length === 1 &&
      filteredOptions.value[0]?.value === targetOption.value &&
      deferredShouldAutoSelect
    ) {
      select(targetOption);
    }
  }, AUTO_SELECT_DELAY_MS);
};

watch(multiModel, () => {
  if (isOpen.value) {
    void nextTick(() => {
      positionOverlay();
    });
  }
});

const currentDisplayedTextValue = computed(() => {
  return autoCompletedText.value || filterText.value;
});

const positionOverlay = () => {
  if (!triggerRef.value) {
    return;
  }
  const rect = triggerRef.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;

  overlayStyle.value =
    spaceBelow < 220 && spaceAbove > spaceBelow
      ? {
          position: "fixed",
          bottom: `${String(window.innerHeight - rect.top + 4)}px`,
          left: `${String(rect.left)}px`,
          minWidth: `${String(rect.width)}px`,
          maxHeight: `${String(spaceAbove - 8)}px`,
          overflowY: "auto",
          zIndex: OVERLAY_Z_INDEX,
        }
      : {
          position: "fixed",
          top: `${String(rect.bottom + 4)}px`,
          left: `${String(rect.left)}px`,
          minWidth: `${String(rect.width)}px`,
          zIndex: OVERLAY_Z_INDEX,
        };
};

const onMouseDown = (e: MouseEvent) => {
  const target = e.target as Node;
  if (overlayRef.value?.contains(target)) {
    return;
  }
  if (
    triggerRef.value?.contains(target) ||
    overlayRef.value?.contains(target) ||
    iconRef.value?.contains(target)
  ) {
    toggle();
  } else {
    close();
  }
};

const onScroll = () => {
  if (isOpen.value) {
    positionOverlay();
  }
};

const handleBlur = () => {
  if (skipAutoSelectOnNextBlur.value) {
    skipAutoSelectOnNextBlur.value = false;
  } else {
    scheduleAutoSelectOnBlur();
  }

  if (model.value !== undefined && autoSelectTimeout === undefined) {
    filterText.value =
      properties.options.find((o) => o.value === model.value)?.label ?? "";
  }
  closePreservingAutoSelect();
};
const handleKeyDown = (event: KeyboardEvent) => {
  if (properties.disabled) {
    return;
  }

  // Allow Shift to pass through for capital letters
  if (event.key === "Shift") {
    return;
  }
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    if (isOpen.value) {
      event.preventDefault();
      const options = displayedOptions.value;
      if (options.length === 0) {
        return;
      }

      const selectedIndex = options.findIndex(
        (option) => option.value === model.value
      );
      let currentIndex = highlightedIndex.value ?? selectedIndex;

      if (currentIndex === -1) {
        currentIndex = event.key === "ArrowDown" ? -1 : 0;
      }

      if (event.key === "ArrowUp" && currentIndex <= 0) {
        // If a filter is active, check for an item above in the unfiltered list
        const activeFilter =
          properties.filterable && filterText.value.trim() !== "";
        if (activeFilter) {
          const firstFilteredOption = options[0];
          const unfilteredIndexOfFirst = firstFilteredOption
            ? properties.options.findIndex(
                (o) => o.value === firstFilteredOption.value
              )
            : -1;
          const prevUnfilteredIndex = unfilteredIndexOfFirst - 1;
          if (prevUnfilteredIndex >= 0) {
            // Clear filter and highlight the item above in the unfiltered list
            const targetOption = properties.options[prevUnfilteredIndex];
            if (!targetOption) {
              return;
            }
            filterText.value = "";
            autoCompletedText.value = "";
            suppressAutoCompleteFromDelete.value = false;
            void nextTick(() => {
              const newOptions = displayedOptions.value;
              const newIndex = newOptions.findIndex(
                (o) => o.value === targetOption.value
              );
              if (newIndex !== -1) {
                if (highlightedIndex.value !== undefined) {
                  const prevEl = overlayRef.value?.querySelectorAll(
                    ".dropdown-option"
                  )[highlightedIndex.value] as HTMLElement | undefined;
                  if (prevEl) prevEl.classList.remove("dropdown-option-hover");
                }
                highlightedIndex.value = newIndex;
                highlightedWithKeyboard.value = true;
                void nextTick(() => {
                  const el = overlayRef.value?.querySelectorAll(
                    ".dropdown-option"
                  )[newIndex] as HTMLElement | undefined;
                  if (el) {
                    el.classList.add("dropdown-option-hover");
                    el.scrollIntoView({ block: "nearest", behavior: "auto" });
                  }
                });
              }
            });
            return;
          }
          // No item above — close
        }
        if (properties.multiselect) {
          close();
          return;
        } else {
          model.value = undefined;
          filterText.value = "";
          autoCompletedText.value = "";
        }
        close();
        return;
      }

      // Remove hover from previous
      if (highlightedIndex.value !== undefined) {
        const prevOption = overlayRef.value?.querySelectorAll(
          ".dropdown-option"
        )[highlightedIndex.value] as HTMLElement | undefined;
        if (prevOption) prevOption.classList.remove("dropdown-option-hover");
      }

      // ArrowDown at the last filtered option: cross boundary into unfiltered list
      if (
        event.key === "ArrowDown" &&
        currentIndex >= options.length - 1 &&
        properties.filterable &&
        filterText.value.trim() !== ""
      ) {
        const lastFilteredOption = options.at(-1);
        const unfilteredIndexOfLast = lastFilteredOption
          ? properties.options.findIndex(
              (o) => o.value === lastFilteredOption.value
            )
          : -1;
        const nextUnfilteredIndex = unfilteredIndexOfLast + 1;
        if (nextUnfilteredIndex < properties.options.length) {
          // Clear filter and highlight the next item in the unfiltered list
          const targetOption = properties.options[nextUnfilteredIndex];
          if (!targetOption) {
            return;
          }
          filterText.value = "";
          autoCompletedText.value = "";
          suppressAutoCompleteFromDelete.value = false;
          void nextTick(() => {
            const newOptions = displayedOptions.value;
            const newIndex = newOptions.findIndex(
              (o) => o.value === targetOption.value
            );
            if (newIndex !== -1) {
              highlightedIndex.value = newIndex;
              highlightedWithKeyboard.value = true;
              void nextTick(() => {
                const el = overlayRef.value?.querySelectorAll(
                  ".dropdown-option"
                )[newIndex] as HTMLElement | undefined;
                if (el) {
                  el.classList.add("dropdown-option-hover");
                  el.scrollIntoView({ block: "nearest", behavior: "auto" });
                }
              });
            }
          });
        }
        // No more items below — no effect
        return;
      }

      const nextIndex =
        event.key === "ArrowDown"
          ? Math.min(currentIndex + 1, options.length - 1)
          : currentIndex - 1;
      highlightedIndex.value = nextIndex;
      highlightedWithKeyboard.value = true;
      // Add hover to new and scroll into view
      void nextTick(() => {
        const nextOption = overlayRef.value?.querySelectorAll(
          ".dropdown-option"
        )[nextIndex] as HTMLElement | undefined;
        if (nextOption) {
          nextOption.classList.add("dropdown-option-hover");
          nextOption.scrollIntoView({ block: "nearest", behavior: "auto" });
        }
      });
    } else {
      event.preventDefault();
      open();
    }
  } else if (event.key === "Enter" && isOpen.value) {
    event.preventDefault();
    // If a keyboard-highlighted option exists, select it
    if (highlightedIndex.value !== undefined) {
      const option = displayedOptions.value[highlightedIndex.value];
      if (option) {
        select(option);
        return;
      }
    }
    // fallback: select current model
    const selectedOption = properties.options.find(
      (option) => option.value === model.value
    );
    if (selectedOption) {
      select(selectedOption);
    }
  } else if (event.key === "Enter" && !isOpen.value) {
    event.preventDefault();
    open();
  } else if (event.key === "Escape") {
    event.preventDefault();
    close();
  } else if (event.key === "Tab") {
    if (
      isOpen.value &&
      highlightedWithKeyboard.value &&
      highlightedIndex.value !== undefined
    ) {
      const option = displayedOptions.value[highlightedIndex.value];
      if (option) {
        skipAutoSelectOnNextBlur.value = true;
        highlightedWithKeyboard.value = false;
        select(option);
      }
    }
    return true;
  } else if (properties.filterable) {
    // Do not manually update filterText for normal typing; let the input handle it
    // Only handle special keys above
    return;
  }
};
const handleMouseEnter = (event: Event) => {
  const target = event.target as HTMLElement;
  const optionElement = target.closest(".dropdown-option");
  if (optionElement) {
    optionElement.classList.add("dropdown-option-hover");
    highlightedWithKeyboard.value = false;
    // Sync highlightedIndex with mouse/focus
    const options = overlayRef.value?.querySelectorAll(".dropdown-option");
    if (options) {
      highlightedIndex.value = [...options].indexOf(optionElement);
    }
  }
};
const onInput = (event: Event) => {
  if (properties.disabled) {
    return;
  }

  if (!isOpen.value) {
    open();
  }
  const nextValue = (event.target as HTMLInputElement).value;
  const previousValue = filterText.value;
  const inputType = "inputType" in event ? (event as InputEvent).inputType : "";

  if (
    inputType.startsWith("delete") ||
    (inputType === "" && nextValue.length < previousValue.length)
  ) {
    suppressAutoCompleteFromDelete.value = true;
  } else if (
    inputType.startsWith("insert") ||
    (inputType === "" && nextValue.length > previousValue.length)
  ) {
    suppressAutoCompleteFromDelete.value = false;
  }

  autoCompletedText.value = "";
  highlightedIndex.value = undefined;
  highlightedWithKeyboard.value = false;
  filterText.value = nextValue;
};

const handleMouseLeave = (event: Event) => {
  const target = event.target as HTMLElement;
  const optionElement = target.closest(".dropdown-option");
  if (optionElement) {
    optionElement.classList.remove("dropdown-option-hover");
    highlightedIndex.value = undefined;
    highlightedWithKeyboard.value = false;
  }
};
onMounted(() => {
  document.addEventListener("mousedown", onMouseDown);
  globalThis.addEventListener("scroll", onScroll, true);
});
onUnmounted(() => {
  if (autoCompleteTimeout) {
    clearTimeout(autoCompleteTimeout);
  }
  clearAutoSelectTimeout();
  document.removeEventListener("mousedown", onMouseDown);
  globalThis.removeEventListener("scroll", onScroll, true);
});
</script>

<template>
  <!-- Filterable + Multiselect: Show pills + input -->
  <template v-if="properties.filterable && properties.multiselect">
    <div
      ref="triggerRef"
      :class="[
        'filterable-multi-select-trigger',
        multiModel.length === 0 && !error
          ? 'filterable-multi-select-trigger-empty'
          : '',
        isOpen ? 'border-[#0053a0] shadow-[0_0_0_3px_rgba(0,83,160,0.1)]' : '',
        properties.disabled || loading ? 'pointer-events-none opacity-60' : '',
      ]"
      :aria-expanded="isOpen"
      :aria-label="properties.ariaLabel"
      :aria-controls="overlayId"
      :aria-disabled="properties.disabled || undefined"
      role="combobox"
      tabindex="-1"
    >
      <!-- Input row for typing/filtering -->
      <div class="filterable-multi-select-input-row">
        <input
          :id="
            properties.id === undefined
              ? Math.random().toString()
              : properties.id
          "
          ref="inputRef"
          type="text"
          class="filterable-multi-select-input"
          :placeholder="
            properties.disabled
              ? ''
              : loading
                ? 'Loading…'
                : multiModel.length === 0
                  ? (properties.placeholder ?? 'Select options…')
                  : ''
          "
          :value="filterText"
          :disabled="properties.disabled"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          aria-autocomplete="none"
          @keydown="handleKeyDown"
          @keydown.escape="close"
          @blur="handleBlur"
          @input="onInput"
        />

        <!-- Chevron icon -->
        <span
          class="dropdown-icon-wrapper filterable-multi-select-chevron"
          aria-hidden="true"
          @mousedown.stop.prevent="toggle"
        >
          <svg
            ref="iconRef"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            class="dropdown-chevron-icon"
            :class="isOpen ? 'rotate-180' : ''"
          >
            <path
              fill-rule="evenodd"
              d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
      </div>

      <!-- Pills for selected options -->
      <div class="filterable-multi-select-pills">
        <span v-if="error" class="text-[13px] text-[#c0392b]">{{ error }}</span>
        <template v-else>
          <span
            v-for="opt in selectedMultiOptions"
            :key="opt.value"
            class="multi-select-pill"
          >
            {{ opt.label }}
            <button
              tabindex="-1"
              class="multi-select-pill-remove"
              aria-label="Remove"
              @mousedown.prevent="removeMultiOption(opt.value, $event)"
            >
              ×
            </button>
          </span>
          <button
            v-if="multiModel.length > 0"
            tabindex="-1"
            class="multi-select-clear"
            aria-label="Clear all"
            @mousedown.prevent="clearAllMulti($event)"
          >
            Clear
          </button>
        </template>
      </div>
    </div>
  </template>

  <!-- Filterable single-select: Show input -->
  <template v-else-if="properties.filterable">
    <div class="filterable-single-select-wrapper">
      <input
        :id="
          properties.id === undefined ? Math.random().toString() : properties.id
        "
        ref="triggerRef"
        type="text"
        class="textbox pe-9"
        :class="[
          isOpen
            ? 'border-[#0053a0] shadow-[0_0_0_3px_rgba(0,83,160,0.1)]'
            : '',
          properties.disabled || loading
            ? 'pointer-events-none opacity-60'
            : '',
          properties.error ? 'dropdown-placeholder-error' : '',
          properties.loading ? 'dropdown-placeholder-loading' : '',
        ]"
        :aria-expanded="isOpen"
        :aria-label="properties.ariaLabel"
        :aria-controls="overlayId"
        :aria-disabled="properties.disabled || undefined"
        :placeholder="placeholder"
        :value="currentDisplayedTextValue"
        :disabled="properties.disabled"
        tabindex="0"
        autocomplete="off"
        autocapitalize="off"
        autocorrect="off"
        spellcheck="false"
        aria-autocomplete="none"
        @keydown="handleKeyDown"
        @keydown.escape="close"
        @blur="handleBlur"
        @input="onInput"
      />
      <span class="dropdown-icon-wrapper" aria-hidden="true">
        <svg
          ref="iconRef"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          class="dropdown-chevron-icon"
          :class="isOpen ? 'rotate-180' : ''"
        >
          <path
            fill-rule="evenodd"
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            clip-rule="evenodd"
          />
        </svg>
      </span>
    </div>
  </template>
  <!-- Non-filterable: Show dropdown-trigger or multi-select-trigger div -->
  <template v-else>
    <div
      ref="triggerRef"
      :class="[
        properties.multiselect ? 'multi-select-trigger' : 'dropdown-trigger',
        isOpen ? 'border-[#0053a0] shadow-[0_0_0_3px_rgba(0,83,160,0.1)]' : '',
        properties.disabled || loading ? 'pointer-events-none opacity-60' : '',
      ]"
      :aria-expanded="isOpen"
      :aria-label="properties.ariaLabel"
      :aria-controls="overlayId"
      :aria-disabled="properties.disabled || undefined"
      role="combobox"
      tabindex="0"
      @keydown.enter.prevent="toggle"
      @keydown.escape="close"
      @keydown="handleKeyDown"
      @blur="handleBlur"
    >
      <!-- Multi-select trigger content -->
      <template v-if="properties.multiselect">
        <div class="multi-select-value-wrap">
          <span v-if="loading" class="text-sm text-slate-500">Loading…</span>
          <span v-else-if="error" class="text-[13px] text-[#c0392b]">{{
            error
          }}</span>
          <span
            v-else-if="multiModel.length === 0 && !properties.disabled"
            class="text-sm text-slate-400"
            >{{ properties.placeholder ?? "All" }}</span
          >
          <template v-else>
            <span
              v-for="opt in selectedMultiOptions"
              :key="opt.value"
              class="multi-select-pill"
            >
              {{ opt.label }}
              <button
                tabindex="-1"
                class="multi-select-pill-remove"
                aria-label="Remove"
                @mousedown.prevent="removeMultiOption(opt.value, $event)"
              >
                ×
              </button>
            </span>
            <button
              tabindex="-1"
              class="multi-select-clear"
              aria-label="Clear all"
              @mousedown.prevent="clearAllMulti($event)"
            >
              Clear
            </button>
          </template>
        </div>
      </template>

      <!-- Single-select trigger content -->
      <template v-else>
        <span class="dropdown-selected-text">
          <span v-if="loading" class="text-slate-500">Loading…</span>
          <span v-else-if="error" class="text-[13px] text-[#c0392b]">{{
            error
          }}</span>
          <span
            v-else-if="properties.options.find((o) => o.value === model)?.label"
            >{{
              properties.options.find((o) => o.value === model)?.label
            }}</span
          >
          <span v-else-if="!properties.disabled" class="text-slate-400">{{
            properties.placeholder ?? "Select an option"
          }}</span>
        </span>
      </template>

      <span class="dropdown-icon-wrapper" aria-hidden="true">
        <svg
          ref="iconRef"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          class="dropdown-chevron-icon"
          :class="isOpen ? 'rotate-180' : ''"
        >
          <path
            fill-rule="evenodd"
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            clip-rule="evenodd"
          />
        </svg>
      </span>
    </div>
  </template>

  <Teleport :to="properties.teleportTo">
    <div
      v-if="isOpen && !error"
      :id="overlayId"
      ref="overlayRef"
      class="dropdown-overlay"
      :style="overlayStyle"
      @mousedown.prevent
    >
      <div
        v-if="loading"
        class="dropdown-options-list px-3 py-2 text-sm text-slate-500"
        data-testid="dropdown-listbox"
      >
        {{ properties.loadingStateText }}
      </div>
      <ul
        v-else
        class="dropdown-options-list"
        role="listbox"
        :aria-multiselectable="properties.multiselect || undefined"
        data-testid="dropdown-listbox"
      >
        <li
          v-if="filteredOptions.length === 0"
          class="dropdown-group-header"
          style="cursor: default; background: #f8fafc"
          aria-disabled="true"
        >
          {{ properties.emptyStateText }}
        </li>
        <template v-for="(groupOptions, group) in filteredGroups" :key="group">
          <li
            v-if="
              properties.onGroupHeaderClick &&
              (properties.getGroupHeaderLabel
                ? properties.getGroupHeaderLabel(group)
                : group) !== '' &&
              groupOptions.length > 0
            "
            role="button"
            tabindex="0"
            class="dropdown-group-header dropdown-group-header--clickable"
            @mousedown.prevent="properties.onGroupHeaderClick(group as string)"
            @keydown.enter="properties.onGroupHeaderClick(group as string)"
          >
            {{
              properties.getGroupHeaderLabel
                ? properties.getGroupHeaderLabel(group)
                : group
            }}
          </li>
          <li
            v-else-if="
              (properties.getGroupHeaderLabel
                ? properties.getGroupHeaderLabel(group)
                : group) !== '' && groupOptions.length > 0
            "
            class="dropdown-group-header"
            style="cursor: default; background: #f8fafc"
            aria-disabled="true"
          >
            {{
              properties.getGroupHeaderLabel
                ? properties.getGroupHeaderLabel(group)
                : group
            }}
          </li>
          <li
            v-for="option in groupOptions"
            :key="option.value"
            class="dropdown-option"
            :class="[
              option.disabled ? 'dropdown-option--disabled' : '',
              properties.multiselect
                ? isMultiSelected(option.value)
                  ? 'bg-[#f4f7ff]'
                  : ''
                : option.value === model
                  ? 'bg-[#eef2fb] font-semibold text-[#0053a0]'
                  : '',
              properties.multiselect ? 'flex items-center' : '',
            ]"
            role="option"
            :aria-selected="
              properties.multiselect
                ? isMultiSelected(option.value)
                : option.value === model
            "
            tabindex="0"
            @mousedown.prevent="select(option)"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
            @focusin="handleMouseEnter"
            @focusout="handleMouseLeave"
          >
            {{ option.label }}
            <span
              v-if="properties.multiselect"
              class="multi-select-checkbox ml-auto shrink-0"
              :class="
                isMultiSelected(option.value)
                  ? 'border-[#2b5daa] bg-[#2b5daa] text-white'
                  : ''
              "
              aria-hidden="true"
            >
              <svg
                v-if="isMultiSelected(option.value)"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
                  clip-rule="evenodd"
                />
              </svg>
            </span>
          </li>
        </template>
      </ul>
    </div>
  </Teleport>
</template>
